/// REACT ///
import { useState } from "react";
/// UUID ///
import { v4 } from "uuid";
/// HOOKS ///
import { useAppDispatch } from "api/hooks/apiHook";
/// ACTIONS ///
import { createRestorePoint } from "api/actions/history";
import { updateLocalSink } from "api/actions/sketch/sink";
import { createSink } from "api/actions/sketch/sink";
import { markQuoteChanged } from "api/actions/quotes/quote";
/// TYPES ///
import { CutoutError } from "api/types/sketch";
import { Counter } from "api/types/sketch";
import { IHistoryActionType } from "api/types/history";
import { Sink } from "api/types/sketch";
import { Vector2 } from "api/types/sketch";
/// COMPONENTS ///
import SaveSink from "components/quote/drawer/sink";
/// VALUES ///
import { UNDERMOUNT } from "values/values";
import { RECTANGLE } from "values/values";
/// EMPTY ///
import { empty_sink } from "values/empty/quote/sink";

interface Props {
  open: boolean,
  setOpen: (open: boolean) => void,
  selected_counter: Counter | null,
  mouse: Vector2
}

export default function CreateStoreSink({
  open,
  setOpen,
  selected_counter,
  mouse
}: Props) {
  const dispatch = useAppDispatch();
  const [sink, setSink] = useState<Sink>({ ...empty_sink });
  const [error, setError] = useState<CutoutError>({});

  const close = () => {
    setOpen(false);
    setError({});
    setSink({ ...empty_sink });
  }

  const save = () => {
    let error: CutoutError = {};
    if (!sink?.length) {
      error.length_error = "Length must be greater than 0";
      error.length_has_error = true;
    }
    if (!sink?.width) {
      error.width_error = "Width must be greater than 0";
      error.width_has_error = true;
    }

    if (error.length_has_error || error.width_has_error) {
      setError(error);
      return;
    }

    const new_sink: Sink = {
      ...sink,
      counter_uuid: selected_counter?.uuid,
      uuid: v4(),
      angle: -90,
      x: Math.round(mouse.X),
      y: Math.round(mouse.Y),
      location: {
        X: Math.round(mouse.X),
        Y: Math.round(mouse.Y)
      }
    }

    dispatch(createSink(new_sink));
    dispatch(markQuoteChanged(selected_counter?.quote));

    dispatch(createRestorePoint(
      {
        undo_action: IHistoryActionType.DELETE_SINK,
        redo_action: IHistoryActionType.CREATE_SINK,
        desc: "Create Sink",
        undo_data: { uuid: new_sink.uuid, counter_uuid: new_sink.counter_uuid },
        redo_data: new_sink
      }
    ));
    close();
  }

  const update = (updated_sink: Sink) => {
    setSink(updated_sink);
    dispatch(updateLocalSink(updated_sink));
  }

  return (
    <SaveSink
      open={open}
      setOpen={setOpen}
      sink_type={sink?.sink_type ?? UNDERMOUNT}
      setSinkType={sink_type => update({ ...sink, sink_type })}
      shape={sink?.shape ?? RECTANGLE}
      setShape={shape => update({ ...sink, shape })}
      faucet_hole_count={sink?.faucet_hole_count ?? 1}
      setFaucetHoleCount={faucet_hole_count => update({ ...sink, faucet_hole_count })}
      length={sink?.length ?? 30}
      setLength={length => update({ ...sink, length })}
      length_error={error?.length_error ?? ""}
      length_has_error={error?.length_has_error ?? false}
      width={sink?.width ?? 26}
      setWidth={width => update({ ...sink, width })}
      width_error={error?.width_error ?? ""}
      width_has_error={error?.width_has_error ?? false}
      save={save}
      cancel={close}
      title="Create Sink Cutout" />
  );
}

